/**
 * Overtime Approval Modal Komponenti
 * 
 * Fazla mesai talebini onaylamak için kullanılan modal komponenti.
 * Talep detaylarını gösterir ve onay işlemi için kullanıcıdan konfirmasyon alır.
 * 
 * Özellikler:
 * - Talep detaylarını gösterir
 * - Uyarı mesajları gösterir
 * - Onay/İptal butonları
 * - Loading durumu
 */

import React, { useState } from 'react';
import { Modal, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { OvertimeDto } from '../../../../types/overtime.types';
import { OVERTIME_MODAL_MESSAGES } from '../../../../constants/overtimeConstants';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';

interface OvertimeApprovalModalProps {
  visible: boolean;
  overtime: OvertimeDto | null;
  onConfirm: (overtimeId: number) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

/**
 * Fazla mesai onaylama modal komponenti
 * @param visible - Modal görünür mü?
 * @param overtime - Onaylanacak overtime kaydı
 * @param onConfirm - Onaylama callback'i
 * @param onCancel - İptal callback'i
 * @param loading - Yükleniyor durumu
 */
const OvertimeApprovalModal: React.FC<OvertimeApprovalModalProps> = ({
  visible,
  overtime,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const { t, i18n } = useTranslation();

  // Mesaj konfigürasyonunu al
  const messages = OVERTIME_MODAL_MESSAGES.approve;

  /**
   * Süreyi formatlar
   */
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours === 0) {
      return `${mins} ${t('overtime.minute', 'dakika')}`;
    }
    
    return mins > 0 
      ? `${hours} ${t('overtime.hour', 'saat')} ${mins} ${t('overtime.minute', 'dakika')}` 
      : `${hours} ${t('overtime.hour', 'saat')}`;
  };

  /**
   * Onaylama işlemini başlatır
   */
  const handleConfirm = async () => {
    if (overtime) {
      await onConfirm(overtime.id);
    }
  };

  if (!overtime) {
    return null;
  }

  return (
    <Modal
      title={
        <div className="d-flex align-items-center gap-2">
          <i className="ti ti-check text-success" style={{ fontSize: '1.5rem' }} />
          <span>{i18n.language === 'en' ? messages.titleEn : messages.title}</span>
        </div>
      }
      open={visible}
      onOk={handleConfirm}
      onCancel={onCancel}
      confirmLoading={loading}
      okText={t('overtime.yesApprove', 'Evet, Onayla')}
      cancelText={t('overtime.cancel', 'İptal')}
      okButtonProps={{ 
        danger: false,
        type: 'primary',
        icon: <i className="ti ti-check me-1" />,
      }}
      cancelButtonProps={{
        disabled: loading,
      }}
      width={600}
      centered
    >
      {/* Talep Detayları */}
      <div className="mb-4">
        <div className="mb-3 pb-3 border-bottom">
          <div className="row">
            <div className="col-6 mb-2">
              <span className="text-muted d-block" style={{ fontSize: '0.875rem' }}>
                {t('overtime.employee', 'Çalışan')}
              </span>
              <strong>
                {overtime.user.firstName} {overtime.user.lastName}
              </strong>
            </div>
            <div className="col-6 mb-2">
              <span className="text-muted d-block" style={{ fontSize: '0.875rem' }}>
                {t('overtime.date', 'Tarih')}
              </span>
              <strong>
                {dayjs(overtime.date).locale(i18n.language).format('DD MMMM YYYY')}
              </strong>
            </div>
            <div className="col-6 mb-2">
              <span className="text-muted d-block" style={{ fontSize: '0.875rem' }}>
                {t('overtime.time', 'Saat')}
              </span>
              <strong>
                {overtime.overtimeStart} - {overtime.overtimeEnd}
              </strong>
            </div>
            <div className="col-6 mb-2">
              <span className="text-muted d-block" style={{ fontSize: '0.875rem' }}>
                {t('overtime.duration', 'Süre')}
              </span>
              <strong>
                {formatDuration(overtime.overtimeDuration)}
              </strong>
            </div>
          </div>
          {overtime.description && (
            <div className="mt-3">
              <span className="text-muted d-block mb-1" style={{ fontSize: '0.875rem' }}>
                {t('overtime.description', 'Açıklama')}
              </span>
              <p className="mb-0">{overtime.description}</p>
            </div>
          )}
        </div>

        {/* Uyarı Mesajları */}
        <Alert
          message={
            <strong>
              {i18n.language === 'en' ? messages.warningTextEn : messages.warningText}
            </strong>
          }
          description={
            <ul className="mb-0 ps-3" style={{ fontSize: '0.875rem' }}>
              {(i18n.language === 'en' ? messages.warningsEn : messages.warnings).map(
                (warning, index) => (
                  <li key={index}>{warning}</li>
                )
              )}
            </ul>
          }
          type="warning"
          showIcon
          icon={<i className="ti ti-alert-triangle" style={{ fontSize: '1.25rem' }} />}
        />
      </div>

      {/* Onay Sorusu */}
      <div className="text-center">
        <p className="mb-0 fw-medium">
          {i18n.language === 'en' ? messages.confirmTextEn : messages.confirmText}
        </p>
      </div>
    </Modal>
  );
};

export default OvertimeApprovalModal;


